import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { TooltipService } from './tooltip.service';

@Directive({
  selector: '[richTooltip]',
})
export class RichTooltipDirective implements OnInit, OnChanges, OnDestroy {
  @Input('richTooltip') tooltipKey = '';

  private iconButton: HTMLElement | null = null;
  private removePointerDownListener?: () => void;
  private removeMouseDownListener?: () => void;
  private removeClickListener?: () => void;
  private removeKeydownListener?: () => void;

  constructor(
    private el: ElementRef<HTMLElement>,
    private tooltipService: TooltipService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.syncIconState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['tooltipKey']) {
      return;
    }

    this.syncIconState();
  }

  ngOnDestroy(): void {
    this.detachIcon();

    this.tooltipService.close();
  }

  private syncIconState(): void {
    if (this.tooltipKey && !this.iconButton) {
      this.iconButton = this.createIconButton();
      this.renderer.appendChild(this.el.nativeElement, this.iconButton);

      this.removePointerDownListener = this.renderer.listen(this.iconButton, 'pointerdown', (event: PointerEvent) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      });

      this.removeMouseDownListener = this.renderer.listen(this.iconButton, 'mousedown', (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      });

      this.removeClickListener = this.renderer.listen(this.iconButton, 'click', (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.tooltipService.toggleByKey(this.iconButton as HTMLElement, this.tooltipKey);
      });

      this.removeKeydownListener = this.renderer.listen(this.iconButton, 'keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.tooltipService.toggleByKey(this.iconButton as HTMLElement, this.tooltipKey);
      });
    }

    if (!this.tooltipKey && this.iconButton) {
      this.detachIcon();
      this.tooltipService.close();
    }
  }

  private detachIcon(): void {
    this.removePointerDownListener?.();
    this.removePointerDownListener = undefined;

    this.removeMouseDownListener?.();
    this.removeMouseDownListener = undefined;

    this.removeClickListener?.();
    this.removeClickListener = undefined;

    this.removeKeydownListener?.();
    this.removeKeydownListener = undefined;

    if (!this.iconButton) {
      return;
    }

    this.renderer.removeChild(this.el.nativeElement, this.iconButton);
    this.iconButton = null;
  }

  private createIconButton(): HTMLElement {
    const icon = this.renderer.createElement('span') as HTMLElement;

    this.renderer.setAttribute(icon, 'role', 'button');
    this.renderer.setAttribute(icon, 'class', 'rich-tooltip');
    this.renderer.setAttribute(icon, 'tabindex', '0');
    this.renderer.setAttribute(icon, 'aria-label', 'Show tooltip information');
    this.renderer.setAttribute(icon, 'aria-haspopup', 'dialog');
    this.renderer.setProperty(icon, 'innerText', 'i');
    this.renderer.setStyle(icon, 'flex-shrink', '0');
    this.renderer.setStyle(icon, 'margin-left', '6px');
    this.renderer.setStyle(icon, 'width', '18px');
    this.renderer.setStyle(icon, 'height', '18px');
    this.renderer.setStyle(icon, 'border', '1px solid var(--inscyd-border)');
    this.renderer.setStyle(icon, 'border-radius', '50%');
    this.renderer.setStyle(icon, 'background', 'var(--inscyd-tile-background)');
    this.renderer.setStyle(icon, 'padding', '0');
    this.renderer.setStyle(icon, 'font-size', '11px');
    this.renderer.setStyle(icon, 'line-height', '16px');
    this.renderer.setStyle(icon, 'cursor', 'pointer');
    this.renderer.setStyle(icon, 'display', 'inline-flex');
    this.renderer.setStyle(icon, 'align-items', 'center');
    this.renderer.setStyle(icon, 'justify-content', 'center');
    this.renderer.setStyle(icon, 'vertical-align', 'middle');
    this.renderer.setStyle(icon, 'user-select', 'none');

    return icon;
  }
}
